/* eslint-disable react-refresh/only-export-components */
import { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { message, Select, Spin } from 'antd';
import debounce from 'lodash/debounce';
import { t } from 'i18next';
import { httpService } from '../utils/apiService';
import PropTypes from 'prop-types';

/**
 * Generic Debounced Select Component
 */
const DebouncedSelect = ({
  fetchOptions,
  debounceTimeout = 800,
  placeholder,
  loadOnOpen = false,
  preload = false,
  ...props
}) => {
  const [fetching, setFetching] = useState(false);
  const [options, setOptions] = useState([]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const fetchRef = useRef(0);

  const loadOptions = useCallback((value = '') => {
    fetchRef.current += 1;
    const fetchId = fetchRef.current;
    setFetching(true);

    fetchOptions(value).then((newOptions) => {
      if (fetchId !== fetchRef.current) return; // Ignore outdated requests
      setOptions(newOptions);
      setFetching(false);
      setHasLoaded(true);
    }).catch(() => {
      if (fetchId !== fetchRef.current) return;
      setFetching(false);
    });
  }, [fetchOptions]);

  const debounceFetcher = useMemo(() => {
    return debounce(loadOptions, debounceTimeout);
  }, [loadOptions, debounceTimeout]);

  // Preload options on mount if preload is true and value exists
  useEffect(() => {
    if (preload && !hasLoaded && !fetching && props.value) {
      loadOptions('');
    }
  }, [preload, hasLoaded, fetching, props.value, loadOptions]);

  const handleOpenChange = (open) => {
    // Load initial options when dropdown opens for the first time
    if (open && loadOnOpen && !hasLoaded && !fetching) {
      loadOptions('');
    }
    
    // Call the original onOpenChange if provided (migrated from onDropdownVisibleChange)
    if (props.onOpenChange) {
      props.onOpenChange(open);
    }
  };

  return (
    <Select
      showSearch
      filterOption={false}
      placeholder={placeholder}
      onSearch={debounceFetcher}
      onOpenChange={handleOpenChange}
      notFoundContent={fetching ? <Spin size="small" /> : t('common:noResultsFound')}
      loading={fetching}
      options={options}
      onChange={(value, option) => {
        console.log('DebouncedSelect onChange - value:', value, 'option:', option);
        if (props.onChange) {
          props.onChange(value, option);
        }
      }}
      {...props}
    />
  );
};

DebouncedSelect.propTypes = {
  fetchOptions: PropTypes.func.isRequired,
  debounceTimeout: PropTypes.number,
  placeholder: PropTypes.string,
  loadOnOpen: PropTypes.bool,
  preload: PropTypes.bool,
};

/**
 * Fetch Locations for Debounced Select
 */
export const fetchLocationList = async ({ operatorId, search, page = 1 }) => {
  if (!operatorId) {
    message.error({
      content: t('common:selectOperatorBeforeLocation'),
      duration: 3,
    });
    return [];
  }

  try {
    const response = await httpService.post(
      `/location/get-location-select?search=${search}&page=${page}`,
      { operatorId }
    );

    return response.data.data.paginatedData.map((location) => {
      const city = location.address?.city;
      const state = location.address?.state;
      const locationSuffix = (city || state) ? ` - ${city || ''}${city && state ? ', ' : ''}${state || ''}` : '';
      return {
        label: `${location.locationName}${locationSuffix}`,
        value: location._id,
        data: location,
      };
    });
  } catch (error) {
    console.error('Error fetching locations:', error);
    return [];
  }
};

/**
 * Fetch Chargers for Debounced Select
 */
export const fetchChargerList = async ({ operatorId, locationId ,search, page = 1 }) => {
  if (!operatorId) {
    message.error({
      content: t('common:selectOperatorBeforeChargers'),
      duration: 3,
    });
    return [];
  }

  try {
    const payload = { operatorId };
    if (locationId) {
      payload.locationId = locationId;
    }

    const response = await httpService.post(
      `/charger/get-charger-select?search=${search}&offset=${page}&limit=200`,
      payload
    );

    return response.data.data.paginatedData.map((charger) => ({
      label: `${charger.name} - ${charger.serialNumber || 'N/A'}`,
      value: charger._id,
      data: charger, // Include the full charger data
    }));
  } catch (error) {
    console.error('Error fetching chargers:', error);
    return [];
  }
};

/**
 * Fetch Customers for Debounced Select
 */
export const fetchCustomerList = async ({ operatorId, search, page = 1 }) => {
  if (!operatorId) {
    message.error({
      content: t('common:selectOperatorBeforeCustomers'),
      duration: 3,
    });
    return [];
  }
  try {
    const safeSearch = search ?? '';
    const response = await httpService.post(
      `/customer/get-customer-select?search=${encodeURIComponent(safeSearch)}&offset=${page}&limit=200`,
      { operatorId }
    );

    return response.data.data.paginatedData.map((customer) => ({
      label: `${customer.fname} ${customer.lname} - ${customer.phoneNumber || 'N/A'}`,
      value: customer._id,
      position: customer.position || '',
      data: customer, // Include the full customer data
    }));
  } catch (error) {
    console.error('Error fetching customers:', error);
    return [];
  }
};

/**
 * Fetch RFID list for Debounced Select (API: /rfid/select-rfid?operatorId=...&search=...)
 */
export const fetchRfidSelect = async ({ operatorId, search = '' }) => {
  if (!operatorId) {
    message.error({
      content: t('common:selectOperatorBeforeRfid'),
      duration: 3,
    });
    return [];
  }

  try {
    const params = new URLSearchParams({ operatorId });
    if (search) params.set('search', search);
    const response = await httpService.get(`/rfid/select-rfid?${params.toString()}`);
    const list = response?.data?.data?.rfids ?? response?.data?.rfids ?? [];
    const arr = Array.isArray(list) ? list : [];
    return arr.map((item) => ({
      label: item.RFID ?? item.rfid ?? item.name ?? item._id,
      value: item._id,
      data: item,
    }));
  } catch (error) {
    console.error('Error fetching RFID select:', error);
    return [];
  }
};

export { DebouncedSelect };
